/**
 * SIL-1346 - the Exchange buyer action gate. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 5.
 *
 * Two pieces, deliberately split:
 *
 *  - `VerificationGateProvider` owns ONE modal instance for the whole app and
 *    nothing else. It does not fetch: mounting it at the root therefore costs
 *    no request, and a page with a banner, a status pill and three gated
 *    buttons still opens exactly one modal, never four stacked ones.
 *  - `useVerificationGate()` is what screens call. It reads the status through
 *    `useBuyerStatus()` (the single endpoint caller) and asks the provider to
 *    open the modal. A screen needs no `useState`, no modal import and no
 *    plumbing.
 *
 * The two branches the spec names, and why they differ:
 *
 *  - `403 BUYER_VERIFICATION_REQUIRED` from a gated action opens
 *    `BuyerVerificationModal` with the "Buying requires a verified buyer
 *    account." callout.
 *  - A `pending` org gets a TOAST instead. Reopening a form the user already
 *    submitted reads as "your submission was lost", and there is nothing for
 *    them to do but wait for the ops review.
 *
 * `guard` lets the SERVER decide. It runs the action and reacts to the 403
 * rather than pre-checking the status and refusing locally, because the API is
 * the enforcement boundary (CLAUDE.md: never hide fields in the client only)
 * and a client-side pre-check that disagrees with it is either a false block
 * or a false allow. `requireVerified` exists for the other shape - an
 * affordance with no request to make yet, e.g. a "Buy now" button that should
 * open the verification modal instead of a purchase dialog.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { BuyerVerificationStatus } from '@siliconx/shared';
import { setUserProperties, track } from '@siliconx/shared/analytics';
import { isBuyerVerified, useBuyerStatus, useRefreshBuyerStatus } from '../../hooks/useBuyerStatus';
import { BuyerVerificationModal } from './BuyerVerificationModal';
import {
  BUYER_GATE_CALLOUT,
  BUYER_GATE_PENDING_TOAST_BODY,
  BUYER_GATE_PENDING_TOAST_TITLE,
} from '@siliconx/shared/ui/buyer-listing/copy/buyerVerification';
import { isBuyerVerificationRequired } from './errors';

/** The only thing the provider exposes: open the one modal it hosts. */
interface VerificationGateHost {
  openModal: (reason: string | null) => void;
}

const VerificationGateContext = createContext<VerificationGateHost | null>(null);

export function VerificationGateProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<{ reason: string | null } | null>(null);

  const host = useMemo<VerificationGateHost>(
    () => ({ openModal: (reason) => setOpen({ reason }) }),
    [],
  );

  return (
    <VerificationGateContext.Provider value={host}>
      {children}
      {open ? <BuyerVerificationModal reason={open.reason} onClose={() => setOpen(null)} /> : null}
    </VerificationGateContext.Provider>
  );
}

export interface VerificationGate {
  /** `undefined` until the status query resolves. */
  status: BuyerVerificationStatus | undefined;
  isVerified: boolean;
  /** Submitted and awaiting the ops review - the toast branch, not the modal. */
  isPending: boolean;
  /**
   * Run a gated action, intercepting `403 BUYER_VERIFICATION_REQUIRED`:
   * resolves to the action's value when it succeeds, to `undefined` when the
   * gate handled a 403 (modal or toast already shown). Any other error is
   * rethrown untouched, so a caller's own `onError`/try-catch still sees it.
   */
  guard: <T>(action: () => Promise<T>, reason?: string) => Promise<T | undefined>;
  /**
   * Pre-flight for an affordance with no request to make yet. Returns true
   * when the caller may proceed; returns false having already shown the modal
   * or the toast.
   */
  requireVerified: (reason?: string) => boolean;
  /**
   * Open the modal directly - the "Get verified" buttons on the banner and the
   * unverified action card. No callout by default: the user asked for the
   * form, they do not need to be told why it appeared.
   */
  openVerification: (reason?: string | null) => void;
}

export function useVerificationGate(): VerificationGate {
  const host = useContext(VerificationGateContext);
  if (host === null) {
    throw new Error('useVerificationGate must be used within <VerificationGateProvider>');
  }

  const { data } = useBuyerStatus();
  const refreshStatus = useRefreshBuyerStatus();
  const status = data?.status;
  // SIL-1529
  useEffect(() => {
    if (status) setUserProperties({ buyer_verif_status: status });
  }, [status]);
  // SIL-1359: a verified org with a stale agreement is NOT `pending` — it
  // must hit the modal branch (agreement-only step), never the toast.
  const needsAgreementReacceptance = data?.needsAgreementReacceptance ?? false;

  // Takes the status explicitly rather than closing over it, so the 403 path
  // can pass the freshly refetched value.
  const blockWith = useCallback(
    (blockedStatus: BuyerVerificationStatus | undefined, reason?: string) => {
      if (blockedStatus === 'pending') {
        track('verification_gate_shown', { gate_result: 'pending_toast' });
        toast.info(BUYER_GATE_PENDING_TOAST_TITLE, { description: BUYER_GATE_PENDING_TOAST_BODY });
        return;
      }
      track('verification_gate_shown', { gate_result: 'modal' });
      host.openModal(reason ?? BUYER_GATE_CALLOUT);
    },
    [host],
  );

  const openVerification = useCallback(
    (reason: string | null = null) => host.openModal(reason),
    [host],
  );

  const requireVerified = useCallback(
    (reason?: string) => {
      if (isBuyerVerified(status, needsAgreementReacceptance)) return true;
      blockWith(status, reason);
      return false;
    },
    [status, needsAgreementReacceptance, blockWith],
  );

  const guard = useCallback(
    async <T,>(action: () => Promise<T>, reason?: string): Promise<T | undefined> => {
      try {
        return await action();
      } catch (e) {
        if (!isBuyerVerificationRequired(e)) throw e;
        const fresh = await refreshStatus().catch(() => status);
        blockWith(fresh ?? status, reason);
        return undefined;
      }
    },
    [refreshStatus, blockWith, status],
  );

  return useMemo(
    () => ({
      status,
      isVerified: isBuyerVerified(status, needsAgreementReacceptance),
      isPending: status === 'pending',
      guard,
      requireVerified,
      openVerification,
    }),
    [status, needsAgreementReacceptance, guard, requireVerified, openVerification],
  );
}
